import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import {
  DEFAULT_REPORT_LAYOUT,
  REPORT_NOTES_SOURCE,
  REPORT_RESULT_ELEMENT,
  REPORT_TEMPLATE_STATUS,
  normalizeReportLayout,
  sameReportLayout,
  type ReportLayoutConfig,
  type ReportLayoutEntry,
  type ReportNotesSource,
} from '@lims/shared-types';
import { reportLayoutConfigSchema } from '@lims/shared-validation';
import { catalogueApi, reportTemplatesApi, type ReportTemplateView } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  BilingualTextField,
  CheckboxField,
  Panel,
  RadioField,
  SelectField,
  SwitchField,
  useToast,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { TEMPLATES_QUERY_KEY, TemplateErrorNotice } from './report-template-parts.js';

/**
 * A template's SECTION LAYOUT (D251) — the structured editor.
 *
 * ===========================================================================
 * A LAYOUT ARRANGES; IT NEVER DEFINES TEST SCIENCE (CLAUDE.md section 4).
 * ===========================================================================
 *
 * Known sections and result elements only, each shown or hidden and moved up
 * or down; a few presentation switches; the notes source. No free-form
 * designer, and no field, unit or formula anywhere: the "specialised test
 * results" element is filled at render time by each test's own result
 * template. The server validates the same shape (`reportLayoutConfigSchema`)
 * and is the one that decides — a disabled control is presentation only.
 */

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

function move<K extends string>(
  entries: readonly ReportLayoutEntry<K>[],
  index: number,
  by: -1 | 1,
): ReportLayoutEntry<K>[] {
  const next = [...entries];
  const target = index + by;
  if (target < 0 || target >= next.length) return next;
  const [entry] = next.splice(index, 1);
  next.splice(target, 0, entry as ReportLayoutEntry<K>);
  return next;
}

function OrderedList<K extends string>({
  label,
  entries,
  labelOf,
  hintOf,
  onChange,
  isDisabled,
}: {
  readonly label: string;
  readonly entries: readonly ReportLayoutEntry<K>[];
  readonly labelOf: (key: K) => string;
  readonly hintOf?: (key: K) => string | null;
  readonly onChange: (entries: ReportLayoutEntry<K>[]) => void;
  readonly isDisabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-inline">
      <p className="text-caption font-medium text-default-700">{label}</p>
      <ol className="flex flex-col divide-y divide-default-100 rounded-medium border border-default-200">
        {entries.map((entry, index) => {
          const hint = hintOf?.(entry.key) ?? null;
          return (
            <li
              key={entry.key}
              data-testid={`layout-entry-${entry.key}`}
              className="flex flex-wrap items-center justify-between gap-inline px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-inline">
                <span className="w-6 shrink-0 text-caption tabular-nums text-default-600">{index + 1}</span>
                <SwitchField
                  label={labelOf(entry.key)}
                  {...(hint ? { description: hint } : {})}
                  value={entry.visible}
                  isDisabled={isDisabled}
                  onChange={(visible) =>
                    onChange(entries.map((item) => (item.key === entry.key ? { ...item, visible } : item)))
                  }
                />
              </div>
              <div className="flex gap-1">
                <ActionButton
                  priority="tertiary"
                  size="sm"
                  aria-label={`${t('reportTemplates.design.moveUp')} — ${labelOf(entry.key)}`}
                  isDisabled={isDisabled || index === 0}
                  onPress={() => onChange(move(entries, index, -1))}
                >
                  {t('reportTemplates.design.moveUp')}
                </ActionButton>
                <ActionButton
                  priority="tertiary"
                  size="sm"
                  aria-label={`${t('reportTemplates.design.moveDown')} — ${labelOf(entry.key)}`}
                  isDisabled={isDisabled || index === entries.length - 1}
                  onPress={() => onChange(move(entries, index, 1))}
                >
                  {t('reportTemplates.design.moveDown')}
                </ActionButton>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function Flags<G extends Record<string, boolean>>({
  title,
  group,
  names,
  onChange,
  isDisabled,
}: {
  readonly title: string;
  readonly group: G;
  readonly names: readonly (keyof G & string)[];
  readonly onChange: (group: G) => void;
  readonly isDisabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="mb-1 text-caption font-medium text-default-700">{title}</legend>
      {names.map((name) => (
        <CheckboxField
          key={name}
          label={t(`reportTemplates.design.${name}`)}
          value={group[name] ?? false}
          isDisabled={isDisabled}
          onChange={(value) => onChange({ ...group, [name]: value })}
        />
      ))}
    </fieldset>
  );
}

export function ReportLayoutPanel({ template }: { readonly template: ReportTemplateView }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const canEdit = can(PERMISSIONS.TEMPLATES_MANAGE) && template.status === REPORT_TEMPLATE_STATUS.ACTIVE;

  const [layout, setLayout] = useState<ReportLayoutConfig>(() => normalizeReportLayout(template.layoutConfig));
  const [notesAr, setNotesAr] = useState(template.notes.ar ?? '');
  const [notesEn, setNotesEn] = useState(template.notes.en ?? '');

  // A new revision from the server replaces the form only when nothing is
  // being edited here; typed changes are never thrown away by a refetch.
  const dirty =
    !sameReportLayout(layout, template.layoutConfig) ||
    notesAr.trim() !== (template.notes.ar ?? '') ||
    notesEn.trim() !== (template.notes.en ?? '');
  useEffect(() => {
    if (dirty) return;
    setLayout(normalizeReportLayout(template.layoutConfig));
    setNotesAr(template.notes.ar ?? '');
    setNotesEn(template.notes.en ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.revision]);

  const update = (change: (draft: Mutable<ReportLayoutConfig>) => void) => {
    const draft = { ...layout } as Mutable<ReportLayoutConfig>;
    change(draft);
    setLayout(draft);
  };

  const save = useMutation({
    mutationFn: () =>
      reportTemplatesApi.update(template.id, {
        // Parsed by the API's own schema: what is sent is what it accepts.
        layoutConfig: reportLayoutConfigSchema.parse(layout),
        notes: { ar: notesAr.trim() || null, en: notesEn.trim() || null },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY });
      // From the server's confirmation, never from the button press.
      show({ tone: 'success', message: t('reportTemplates.design.saved') });
    },
  });

  const submit = () => {
    // The schema the API validates with. A layout it refuses is never sent.
    if (!reportLayoutConfigSchema.safeParse(layout).success) return;
    save.mutate();
  };

  const isDefaultLayout = sameReportLayout(layout, DEFAULT_REPORT_LAYOUT);

  return (
    <Panel title={t('reportTemplates.design.title')} description={t('reportTemplates.design.description')}>
      <div className="flex flex-col gap-gutter">
        {canEdit ? null : <p className="text-caption text-default-600">{t('reportTemplates.design.readOnly')}</p>}

        <div className="grid gap-gutter lg:grid-cols-2">
          <OrderedList
            label={t('reportTemplates.design.sections')}
            entries={layout.sections}
            labelOf={(key) => t(`reportTemplates.design.section.${key}`)}
            isDisabled={!canEdit}
            onChange={(sections) => update((draft) => { draft.sections = sections; })}
          />
          <OrderedList
            label={t('reportTemplates.design.resultElements')}
            entries={layout.resultElements}
            labelOf={(key) => t(`reportTemplates.design.element.${key}`)}
            hintOf={(key) =>
              key === REPORT_RESULT_ELEMENT.SPECIALISED_RESULTS ? t('reportTemplates.design.specialisedHint') : null
            }
            isDisabled={!canEdit}
            onChange={(resultElements) => update((draft) => { draft.resultElements = resultElements; })}
          />
        </div>

        <div className="grid gap-gutter sm:grid-cols-2 lg:grid-cols-4">
          <Flags
            title={t('reportTemplates.design.headerOptions')}
            group={layout.header}
            names={['showLogo', 'showAddress', 'showContacts', 'showRegistrations']}
            isDisabled={!canEdit}
            onChange={(header) => update((draft) => { draft.header = header; })}
          />
          <Flags
            title={t('reportTemplates.design.reportInformation')}
            group={layout.reportInformation}
            names={['showReportNumber', 'showDateReported', 'showWorkOrder', 'showClient', 'showProject', 'showProjectLocation']}
            isDisabled={!canEdit}
            onChange={(reportInformation) => update((draft) => { draft.reportInformation = reportInformation; })}
          />
          <Flags
            title={t('reportTemplates.design.signatures')}
            group={layout.signatures}
            names={['testedBy', 'reviewedBy', 'approvedBy']}
            isDisabled={!canEdit}
            onChange={(signatures) => update((draft) => { draft.signatures = signatures; })}
          />
          <Flags
            title={t('reportTemplates.design.footer')}
            group={layout.footer}
            names={['showFooterText', 'showReportIdentity', 'pageNumbering']}
            isDisabled={!canEdit}
            onChange={(footer) => update((draft) => { draft.footer = footer; })}
          />
        </div>

        <SwitchField
          label={t('reportTemplates.design.showQr')}
          description={t('reportTemplates.design.showQrHint')}
          value={layout.showQr}
          isDisabled={!canEdit}
          onChange={(showQr) => update((draft) => { draft.showQr = showQr; })}
        />

        <div className="flex flex-col gap-stack">
          <RadioField
            label={t('reportTemplates.design.notesSource')}
            orientation="horizontal"
            value={layout.notesSource}
            isDisabled={!canEdit}
            onChange={(value) => update((draft) => { draft.notesSource = value as ReportNotesSource; })}
            options={[
              { value: REPORT_NOTES_SOURCE.NONE, label: t('reportTemplates.design.notesSourceNone') },
              { value: REPORT_NOTES_SOURCE.TEMPLATE, label: t('reportTemplates.design.notesSourceTemplate') },
              { value: REPORT_NOTES_SOURCE.LABORATORY, label: t('reportTemplates.design.notesSourceLaboratory') },
            ]}
          />
          {layout.notesSource === REPORT_NOTES_SOURCE.TEMPLATE ? (
            <BilingualTextField
              labelAr={t('reportTemplates.design.notesAr')}
              labelEn={t('reportTemplates.design.notesEn')}
              valueAr={notesAr}
              valueEn={notesEn}
              onChangeAr={setNotesAr}
              onChangeEn={setNotesEn}
              descriptionAr={t('reportTemplates.design.notesHint')}
              descriptionEn={t('reportTemplates.design.notesHint')}
              rows={3}
              isReadOnly={!canEdit}
            />
          ) : null}
        </div>

        {save.error ? <TemplateErrorNotice error={save.error} /> : null}
        {canEdit ? (
          <div className="flex flex-col gap-inline">
            <p className="text-caption text-default-600">{t('reportTemplates.design.revisionNote')}</p>
            <ActionBar>
              <ActionButton
                priority="tertiary"
                isDisabled={isDefaultLayout || save.isPending}
                onPress={() => setLayout(normalizeReportLayout(DEFAULT_REPORT_LAYOUT))}
              >
                {t('reportTemplates.design.reset')}
              </ActionButton>
              <ActionButton priority="primary" isDisabled={!dirty} isLoading={save.isPending} onPress={submit}>
                {t('reportTemplates.design.save')}
              </ActionButton>
            </ActionBar>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// The tests that name this template as their own (D254)
// ---------------------------------------------------------------------------

export function TemplateTestsPanel({ template }: { readonly template: ReportTemplateView }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();
  const canAssign = can(PERMISSIONS.CATALOGUE_MANAGE) && template.status === REPORT_TEMPLATE_STATUS.ACTIVE;
  const [choice, setChoice] = useState<string | null>(null);

  const catalogue = useQuery({
    queryKey: ['catalogue', 'report-template-assign'],
    queryFn: () => catalogueApi.list({ pageSize: 100, includeInactive: false }),
    enabled: canAssign,
  });

  const nameOf = (name: { ar: string | null; en: string | null }) =>
    (locale === 'ar' ? name.ar ?? name.en : name.en ?? name.ar) ?? t('common.notRecorded');

  const assigned = useMemo(() => new Set(template.catalogueItems.map((item) => item.id)), [template.catalogueItems]);
  const options = (catalogue.data?.data ?? [])
    .filter((item) => !assigned.has(item.id))
    .map((item) => ({
      value: item.id,
      label: [item.code, nameOf(item.name)].filter(Boolean).join(' · '),
    }));

  const link = useMutation({
    mutationFn: ({ itemId, templateId }: { itemId: string; templateId: string | null }) =>
      catalogueApi.update(itemId, { reportTemplateId: templateId }),
    onSuccess: async (_item, variables) => {
      await queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ['catalogue'] });
      setChoice(null);
      show({
        tone: 'success',
        message: variables.templateId ? t('reportTemplates.tests.assigned') : t('reportTemplates.tests.removed'),
      });
    },
  });

  return (
    <Panel title={t('reportTemplates.tests.title')} description={t('reportTemplates.tests.description')}>
      <div className="flex flex-col gap-stack">
        {template.catalogueItems.length === 0 ? (
          <p className="text-caption text-default-600">{t('reportTemplates.tests.empty')}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-default-100" data-testid="template-tests">
            {template.catalogueItems.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center justify-between gap-inline py-2">
                <span className="min-w-0">
                  {item.code ? (
                    <span className="me-2 font-mono text-caption" dir="ltr">
                      {item.code}
                    </span>
                  ) : null}
                  <bdi>{nameOf(item.name)}</bdi>
                  {item.isActive ? null : (
                    <span className="ms-2 text-caption text-default-600">{t('reportTemplates.tests.inactive')}</span>
                  )}
                </span>
                {canAssign ? (
                  <ActionButton
                    priority="tertiary"
                    size="sm"
                    isLoading={link.isPending && link.variables?.itemId === item.id}
                    onPress={() => link.mutate({ itemId: item.id, templateId: null })}
                  >
                    {t('reportTemplates.tests.remove')}
                  </ActionButton>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {canAssign ? (
          <div className="flex flex-wrap items-end gap-inline">
            <div className="min-w-0 flex-1">
              <SelectField
                label={t('reportTemplates.tests.choose')}
                placeholder={t('common.notSet')}
                value={choice}
                onChange={setChoice}
                isLoading={catalogue.isLoading}
                options={options}
              />
            </div>
            <ActionButton
              priority="secondary"
              isDisabled={choice === null}
              isLoading={link.isPending && link.variables?.itemId === choice}
              onPress={() => choice && link.mutate({ itemId: choice, templateId: template.id })}
            >
              {t('reportTemplates.tests.assign')}
            </ActionButton>
          </div>
        ) : null}
        {link.error ? <TemplateErrorNotice error={link.error} /> : null}
      </div>
    </Panel>
  );
}
